import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@arcadeai/design-system/components/ui/atoms/dropdown-menu";
import { Link } from "@tanstack/react-router";
import { Loader2, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  useOrgPermissions,
  useProjectPermissions,
} from "@/app/capabilities/api/hooks";
import { ORG, PROJECT } from "@/app/capabilities/permissions";
import type { ScopedBinding } from "@/app/types";
import { useDeletePlugin } from "../../api/hooks";
import type { Plugin } from "../../types";

type SystemActionsProps = {
  plugin: Plugin;
  binding: ScopedBinding;
};

export const SystemActions = ({ plugin, binding }: SystemActionsProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const deletePlugin = useDeletePlugin(binding);
  const { canProject } = useProjectPermissions();
  const { canOrg } = useOrgPermissions();

  // Tenant-bound plugins hit /orgs/:org/plugins (org-scoped); project-bound hit
  // /projects/:id/plugins. Gate edit/delete with the dictionary for its scope.
  const isTenant = binding === "tenant";
  const canEdit = isTenant
    ? canOrg(ORG.plugins.update)
    : canProject(PROJECT.plugins.update);
  const canDelete = isTenant
    ? canOrg(ORG.plugins.delete)
    : canProject(PROJECT.plugins.delete);

  const handleDelete = async () => {
    try {
      await deletePlugin.mutateAsync(plugin.id);
      setIsDeleteDialogOpen(false);
    } catch {
      // Error is handled by the mutation's error handler (global toast)
      // Keep dialog open on failure so user can retry
    }
  };

  // No editable/deletable affordance for this scope — render nothing rather than
  // an empty menu.
  if (!(canEdit || canDelete)) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger render={<Button size="icon" variant="ghost" />}>
          <MoreHorizontal className="size-4" />
          <span className="sr-only">Actions</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canEdit && (
            <DropdownMenuItem
              render={
                <Link
                  from="/orgs/$organizationId/projects/$projectId/contextual-access/$pluginId"
                  params={{ pluginId: plugin.id }}
                  to="."
                />
              }
            >
              <Pencil className="mr-2 size-4" />
              Edit
            </DropdownMenuItem>
          )}
          {canDelete && (
            <>
              {canEdit && <DropdownMenuSeparator />}
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2 className="mr-2 size-4" />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <Dialog onOpenChange={setIsDeleteDialogOpen} open={isDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="mb-2">Delete Extension</DialogTitle>
            <DialogDescription>
              This will permanently delete "{plugin.name}" and all associated
              rules. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 flex justify-end gap-2">
            <Button
              onClick={() => setIsDeleteDialogOpen(false)}
              size="sm"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={deletePlugin.isPending}
              onClick={handleDelete}
              size="sm"
              variant="destructive"
            >
              {deletePlugin.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Delete"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
